import React, { useEffect, useState } from 'react';
import { useNotify } from 'react-admin';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronUp, Plus, Trash2, Save, X, ChevronLeft, ChevronRight } from 'lucide-react';
import {
  createNcr,
  updateNcr,
  getNcrById,
  type Ncr,
  type NcrStage,
} from '../../services/ncrService.ts';
import { getAllNcrTemplates, type NcrTemplate } from '../../services/ncrTemplateService.ts';
import './NcrEdit.css';

const NcrEdit: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const notify = useNotify();
  const isCreate = !id;

  // Form State
  const [formData, setFormData] = useState<Partial<Ncr>>({
    ncrNumber: '',
    description: '',
    templateId: 0,
    ownerId: undefined,
    status: 'draft',
    stages: [],
  });

  const [templates, setTemplates] = useState<NcrTemplate[]>([]);
  const [loading, setLoading] = useState(isCreate ? false : true);
  const [submitting, setSubmitting] = useState(false);
  const [expandedStages, setExpandedStages] = useState<Set<number>>(new Set());
  const [workflowModalOpen, setWorkflowModalOpen] = useState(false);
  const [workflowDraftLevels, setWorkflowDraftLevels] = useState<number[]>([]);

  // Load data on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Load templates
        const templateList = await getAllNcrTemplates();
        setTemplates(templateList);

        // Load NCR if editing
        if (!isCreate && id) {
          const ncrData = await getNcrById(Number(id));
          setFormData(ncrData);
          // Auto-expand first stage for viewing
          if (ncrData.stages && ncrData.stages.length > 0) {
            setExpandedStages(new Set([ncrData.stages[0].id || 0]));
          }
        }
      } catch (error: any) {
        notify(error.message || 'Failed to load data', { type: 'warning' });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, isCreate, notify]);

  // Handle basic field changes
  const handleFieldChange = (field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Handle template selection - load stages from template
  const handleTemplateChange = (templateId: number) => {
    const selectedTemplate = templates.find((t) => t.id === templateId);
    if (selectedTemplate) {
      const newStages: NcrStage[] = selectedTemplate.stages.map((tStage, index) => ({
        templateStageId: tStage.id,
        stageName: tStage.name,
        status: 'pending',
        sortOrder: index,
        actions: [],
        properties: [],
      }));

      setFormData((prev) => ({
        ...prev,
        templateId,
        templateName: selectedTemplate.name,
        stages: newStages,
      }));

      // Clear expanded stages
      setExpandedStages(new Set());
      // Auto-expand first stage
      if (newStages.length > 0) {
        setExpandedStages(new Set([0]));
      }

      notify('Stages loaded from template', { type: 'info' });
    }
  };

  // Toggle stage expansion
  const toggleStageExpanded = (index: number) => {
    const newExpanded = new Set(expandedStages);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedStages(newExpanded);
  };

  // Update stage field
  const updateStageField = (stageIndex: number, field: string, value: any) => {
    setFormData((prev) => {
      const stages = [...(prev.stages || [])];
      stages[stageIndex] = {
        ...stages[stageIndex],
        [field]: value,
      };
      return { ...prev, stages };
    });
  };

  // Update stage property
  const updateStageProperty = (stageIndex: number, propIndex: number, field: string, value: any) => {
    setFormData((prev) => {
      const stages = [...(prev.stages || [])];
      const properties = [...(stages[stageIndex]?.properties || [])];
      properties[propIndex] = {
        ...properties[propIndex],
        [field]: value,
      };
      stages[stageIndex] = {
        ...stages[stageIndex],
        properties,
      };
      return { ...prev, stages };
    });
  };

  // Add property to stage (from template)
  const addPropertyToStage = (stageIndex: number) => {
    setFormData((prev) => {
      const stages = [...(prev.stages || [])];
      const currentStage = stages[stageIndex];
      const templateStage = templates
        .find((t) => t.id === formData.templateId)
        ?.stages.find((s) => s.id === currentStage?.templateStageId);

      if (templateStage && templateStage.children && templateStage.children.length > 0) {
        // This is a simplified version - you may need to adjust based on your template structure
        stages[stageIndex] = {
          ...currentStage,
          properties: [
            ...(currentStage?.properties || []),
            {
              templatePropertyId: undefined,
              propertyName: '',
              value: '',
              attachments: [],
            },
          ],
        };
      }
      return { ...prev, stages };
    });
  };

  // Remove property from stage
  const removePropertyFromStage = (stageIndex: number, propIndex: number) => {
    setFormData((prev) => {
      const stages = [...(prev.stages || [])];
      const properties = [...(stages[stageIndex]?.properties || [])];
      properties.splice(propIndex, 1);
      stages[stageIndex] = {
        ...stages[stageIndex],
        properties,
      };
      return { ...prev, stages };
    });
  };

  // Add action to stage
  const addActionToStage = (stageIndex: number) => {
    setFormData((prev) => {
      const stages = [...(prev.stages || [])];
      const currentStage = stages[stageIndex];
      stages[stageIndex] = {
        ...currentStage,
        actions: [
          ...(currentStage?.actions || []),
          {
            requestDesc: '',
            responseDesc: '',
            ownerId: undefined,
            status: 'pending',
            attachments: [],
          },
        ],
      };
      return { ...prev, stages };
    });
  };

  // Update stage action field
  const updateStageAction = (stageIndex: number, actionIndex: number, field: string, value: any) => {
    setFormData((prev) => {
      const stages = [...(prev.stages || [])];
      const actions = [...(stages[stageIndex]?.actions || [])];
      actions[actionIndex] = {
        ...actions[actionIndex],
        [field]: value,
      };
      stages[stageIndex] = {
        ...stages[stageIndex],
        actions,
      };
      return { ...prev, stages };
    });
  };

  // Remove action from stage
  const removeActionFromStage = (stageIndex: number, actionIndex: number) => {
    setFormData((prev) => {
      const stages = [...(prev.stages || [])];
      const actions = [...(stages[stageIndex]?.actions || [])];
      actions.splice(actionIndex, 1);
      stages[stageIndex] = {
        ...stages[stageIndex],
        actions,
      };
      return { ...prev, stages };
    });
  };

  // Workflow config modal handlers (Qpulse-style left/right indent)
  const openWorkflowConfig = () => {
    const stages = formData.stages || [];
    if (stages.length === 0) {
      notify('Please select template and load stages first', { type: 'warning' });
      return;
    }
    const initialLevels = stages.map((stage) => Math.max(0, Number(stage.workflowLevel ?? 0)));
    if (initialLevels.length > 0) {
      initialLevels[0] = 0;
    }
    setWorkflowDraftLevels(initialLevels);
    setWorkflowModalOpen(true);
  };

  const moveStageLeft = (index: number) => {
    if (index === 0) return;
    setWorkflowDraftLevels((prev) => {
      const next = [...prev];
      next[index] = Math.max(0, (next[index] ?? 0) - 1);
      return next;
    });
  };

  const moveStageRight = (index: number) => {
    if (index === 0) return;
    setWorkflowDraftLevels((prev) => {
      const next = [...prev];
      const maxAllowed = (next[index - 1] ?? 0) + 1;
      next[index] = Math.min(maxAllowed, (next[index] ?? 0) + 1);
      return next;
    });
  };

  const applyWorkflowConfig = () => {
    setFormData((prev) => {
      const stages = [...(prev.stages || [])];
      const normalizedLevels = workflowDraftLevels.map((level, index) => {
        if (index === 0) return 0;
        const maxAllowed = (workflowDraftLevels[index - 1] ?? 0) + 1;
        return Math.max(0, Math.min(level ?? 0, maxAllowed));
      });

      const nextStages = stages.map((stage, index) => {
        const level = normalizedLevels[index] ?? 0;
        let parentIndex = -1;
        if (level > 0) {
          for (let i = index - 1; i >= 0; i -= 1) {
            if ((normalizedLevels[i] ?? 0) === level - 1) {
              parentIndex = i;
              break;
            }
          }
        }

        const parentStage = parentIndex >= 0 ? stages[parentIndex] : undefined;

        return {
          ...stage,
          workflowLevel: level,
          parentStageId: parentStage?.id,
          parentTemplateStageId: parentStage?.templateStageId,
        };
      });

      return {
        ...prev,
        stages: nextStages,
      };
    });

    setWorkflowModalOpen(false);
    notify('Workflow configured successfully', { type: 'info' });
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.ncrNumber?.trim()) {
      notify('NCR Number is required', { type: 'warning' });
      return;
    }

    if (!formData.templateId) {
      notify('Template selection is required', { type: 'warning' });
      return;
    }

    setSubmitting(true);
    try {
      const submitData = {
        ...formData,
        stages: formData.stages?.map((stage) => ({
          templateStageId: stage.templateStageId,
          parentStageId: stage.parentStageId,
          parentTemplateStageId: stage.parentTemplateStageId,
          workflowLevel: stage.workflowLevel ?? 0,
          stageName: stage.stageName,
          ownerId: stage.ownerId,
          status: stage.status,
          properties: stage.properties,
          actions: stage.actions || [],
        })),
      };

      if (isCreate) {
        const result = await createNcr(submitData);
        notify('NCR created successfully', { type: 'info' });
        navigate(`/Ncr/${result.id}`);
      } else {
        await updateNcr(Number(id), submitData);
        notify('NCR updated successfully', { type: 'info' });
        navigate(`/Ncr/${id}`);
      }
    } catch (error: any) {
      notify(error.message || 'Failed to save NCR', { type: 'warning' });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="ncr-edit-loading">Loading...</div>;
  }

  return (
    <form onSubmit={handleSubmit} className="ncr-edit-form">
      <div className="ncr-edit-header">
        <h1>{isCreate ? 'Create New NCR' : 'Edit NCR'}</h1>
        <div className="header-actions">
          <button
            type="button"
            className="btn-secondary"
            onClick={() => navigate('/Ncr')}
          >
            <X size={18} /> Cancel
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={submitting}
          >
            <Save size={18} /> {submitting ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      <div className="ncr-edit-content">
        {/* NCR Basic Information */}
        <div className="form-section">
          <h2>NCR Information</h2>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="ncrNumber">NCR Number *</label>
              <input
                id="ncrNumber"
                type="text"
                value={formData.ncrNumber || ''}
                onChange={(e) => handleFieldChange('ncrNumber', e.target.value)}
                placeholder="e.g., NCR-2024-001"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="templateId">Select Template *</label>
              <select
                id="templateId"
                value={formData.templateId || 0}
                onChange={(e) => handleTemplateChange(Number(e.target.value))}
                required
              >
                <option value={0}>-- Select a Template --</option>
                {templates.map((template) => (
                  <option key={template.id} value={template.id}>
                    {template.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group full-width">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                value={formData.description || ''}
                onChange={(e) => handleFieldChange('description', e.target.value)}
                placeholder="Enter NCR description"
                rows={3}
              />
            </div>

            <div className="form-group">
              <label htmlFor="ownerId">Owner ID</label>
              <input
                id="ownerId"
                type="number"
                value={formData.ownerId || ''}
                onChange={(e) => handleFieldChange('ownerId', e.target.value ? Number(e.target.value) : undefined)}
                placeholder="Enter owner ID"
              />
            </div>

            <div className="form-group">
              <label htmlFor="status">Status</label>
              <select
                id="status"
                value={formData.status || 'draft'}
                onChange={(e) => handleFieldChange('status', e.target.value)}
              >
                <option value="draft">Draft</option>
                <option value="in-progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="closed">Closed</option>
              </select>
            </div>
          </div>
        </div>

        {/* Stages Section */}
        {formData.stages && formData.stages.length > 0 && (
          <div className="form-section">
            <div className="section-header-with-action">
              <h2>Stages ({formData.stages.length})</h2>
              <button
                type="button"
                className="btn-sm btn-primary"
                onClick={openWorkflowConfig}
              >
                Config Workflow
              </button>
            </div>
            <div className="stages-list">
              {formData.stages.map((stage, stageIndex) => (
                <div key={stageIndex} className="stage-card">
                  <div
                    className="stage-header"
                    onClick={() => toggleStageExpanded(stageIndex)}
                  >
                    <div className="stage-title-section">
                      {expandedStages.has(stageIndex) ? (
                        <ChevronUp size={20} />
                      ) : (
                        <ChevronDown size={20} />
                      )}
                      <h3 style={{ marginLeft: `${(stage.workflowLevel ?? 0) * 20}px` }}>
                        Stage {stageIndex + 1}: {stage.stageName}
                        <span className="stage-level-badge">L{stage.workflowLevel ?? 0}</span>
                      </h3>
                    </div>
                    <span className={`stage-status stage-status-${stage.status}`}>
                      {stage.status}
                    </span>
                  </div>

                  {expandedStages.has(stageIndex) && (
                    <div className="stage-content">
                      <div className="form-grid">
                        <div className="form-group">
                          <label>Owner ID</label>
                          <input
                            type="number"
                            value={stage.ownerId || ''}
                            onChange={(e) =>
                              updateStageField(
                                stageIndex,
                                'ownerId',
                                e.target.value ? Number(e.target.value) : undefined
                              )
                            }
                            placeholder="Enter stage owner ID"
                          />
                        </div>

                        <div className="form-group">
                          <label>Status</label>
                          <select
                            value={stage.status || 'pending'}
                            onChange={(e) => updateStageField(stageIndex, 'status', e.target.value)}
                          >
                            <option value="pending">Pending</option>
                            <option value="in-progress">In Progress</option>
                            <option value="completed">Completed</option>
                          </select>
                        </div>
                      </div>

                      {/* Properties */}
                      {stage.properties && stage.properties.length > 0 && (
                        <div className="properties-section">
                          <div className="properties-header">
                            <h4>Properties</h4>
                            <button
                              type="button"
                              className="btn-sm btn-secondary"
                              onClick={() => addPropertyToStage(stageIndex)}
                            >
                              <Plus size={14} /> Add Property
                            </button>
                          </div>

                          <table className="properties-table">
                            <thead>
                              <tr>
                                <th>Property Name</th>
                                <th>Value</th>
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {stage.properties.map((prop, propIndex) => (
                                <tr key={propIndex}>
                                  <td>
                                    <input
                                      type="text"
                                      value={prop.propertyName || ''}
                                      onChange={(e) =>
                                        updateStageProperty(
                                          stageIndex,
                                          propIndex,
                                          'propertyName',
                                          e.target.value
                                        )
                                      }
                                      placeholder="Property name"
                                    />
                                  </td>
                                  <td>
                                    <input
                                      type="text"
                                      value={prop.value || ''}
                                      onChange={(e) =>
                                        updateStageProperty(
                                          stageIndex,
                                          propIndex,
                                          'value',
                                          e.target.value
                                        )
                                      }
                                      placeholder="Property value"
                                    />
                                  </td>
                                  <td>
                                    <button
                                      type="button"
                                      className="btn-icon btn-delete"
                                      onClick={() => removePropertyFromStage(stageIndex, propIndex)}
                                    >
                                      <Trash2 size={14} />
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}

                      {/* Actions */}
                      <div className="properties-section">
                        <div className="properties-header">
                          <h4>Actions</h4>
                          <button
                            type="button"
                            className="btn-sm btn-secondary"
                            onClick={() => addActionToStage(stageIndex)}
                          >
                            <Plus size={14} /> Add Action
                          </button>
                        </div>

                        {(stage.actions?.length || 0) === 0 ? (
                          <p style={{ margin: 0, color: '#666', fontSize: '13px' }}>
                            No actions yet. Click "Add Action" to create one.
                          </p>
                        ) : (
                          <table className="properties-table">
                            <thead>
                              <tr>
                                <th>Request Desc</th>
                                <th>Response Desc</th>
                                <th>Owner ID</th>
                                <th>Status</th>
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {stage.actions?.map((action, actionIndex) => (
                                <tr key={actionIndex}>
                                  <td>
                                    <input
                                      type="text"
                                      value={action.requestDesc || ''}
                                      onChange={(e) =>
                                        updateStageAction(
                                          stageIndex,
                                          actionIndex,
                                          'requestDesc',
                                          e.target.value
                                        )
                                      }
                                      placeholder="Request description"
                                    />
                                  </td>
                                  <td>
                                    <input
                                      type="text"
                                      value={action.responseDesc || ''}
                                      onChange={(e) =>
                                        updateStageAction(
                                          stageIndex,
                                          actionIndex,
                                          'responseDesc',
                                          e.target.value
                                        )
                                      }
                                      placeholder="Response description"
                                    />
                                  </td>
                                  <td>
                                    <input
                                      type="number"
                                      value={action.ownerId || ''}
                                      onChange={(e) =>
                                        updateStageAction(
                                          stageIndex,
                                          actionIndex,
                                          'ownerId',
                                          e.target.value ? Number(e.target.value) : undefined
                                        )
                                      }
                                      placeholder="Owner ID"
                                    />
                                  </td>
                                  <td>
                                    <select
                                      value={action.status || 'pending'}
                                      onChange={(e) =>
                                        updateStageAction(
                                          stageIndex,
                                          actionIndex,
                                          'status',
                                          e.target.value
                                        )
                                      }
                                    >
                                      <option value="pending">Pending</option>
                                      <option value="in-progress">In Progress</option>
                                      <option value="completed">Completed</option>
                                    </select>
                                  </td>
                                  <td>
                                    <button
                                      type="button"
                                      className="btn-icon btn-delete"
                                      onClick={() => removeActionFromStage(stageIndex, actionIndex)}
                                    >
                                      <Trash2 size={14} />
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {!formData.templateId && (
          <div className="empty-state">
            <p>Please select a template to view and configure stages</p>
          </div>
        )}
      </div>

      {workflowModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content workflow-modal">
            <div className="modal-header">
              <h3>Config Workflow</h3>
              <button
                type="button"
                className="btn-close"
                onClick={() => setWorkflowModalOpen(false)}
              >
                <X size={20} />
              </button>
            </div>

            <div className="modal-body">
              <p className="workflow-tip">
                Use left/right buttons to set hierarchy. Right = child level, Left = parent level.
              </p>
              <div className="workflow-list">
                {(formData.stages || []).map((stage, index) => {
                  const level = workflowDraftLevels[index] ?? 0;
                  const maxRight = index === 0 ? 0 : (workflowDraftLevels[index - 1] ?? 0) + 1;
                  return (
                    <div key={`${stage.templateStageId || stage.id || index}-${index}`} className="workflow-row">
                      <div className="workflow-row-actions">
                        <button
                          type="button"
                          className="btn-icon workflow-indent-btn"
                          onClick={() => moveStageLeft(index)}
                          disabled={index === 0 || level === 0}
                          title="Move Left"
                        >
                          <ChevronLeft size={16} />
                        </button>
                        <button
                          type="button"
                          className="btn-icon workflow-indent-btn"
                          onClick={() => moveStageRight(index)}
                          disabled={index === 0 || level >= maxRight}
                          title="Move Right"
                        >
                          <ChevronRight size={16} />
                        </button>
                      </div>

                      <div className="workflow-stage-name" style={{ marginLeft: `${level * 24}px` }}>
                        <span className="workflow-stage-order">{index + 1}.</span>
                        <span>{stage.stageName || `Stage ${index + 1}`}</span>
                        <span className="workflow-level-tag">L{level}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setWorkflowModalOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={applyWorkflowConfig}
              >
                Apply Workflow
              </button>
            </div>
          </div>
        </div>
      )}
    </form>
  );
};

export default NcrEdit;
